import { Document, Packer, Paragraph, TextRun, HeadingLevel } from "docx";
import { saveAs } from "file-saver";

import { DocumentInfo, DocumentSection } from "../../models/documents";
import {
    TBudgetProfile,
    TReadBudgetsResponse,
} from "../../services/budget/read-budgets";
import { TReadBudgetsLevelsDetailsByIdResponse } from "../../services/budget/read-budgets-levels-detailed-by-id";
import { generateDocxSections } from "./docxSections";

/**
 * Generates and downloads a DOCX document based on provided sections and document info
 */
export const generateDOCX = async (
    sections: DocumentSection[],
    documentInfo: DocumentInfo,
    profiles: TBudgetProfile[],
    budgetData: TReadBudgetsResponse,
    tableScopeData: TReadBudgetsLevelsDetailsByIdResponse
): Promise<void> => {
    try {
        const formattedDate = new Date().toLocaleDateString("pt-BR");

        // Criar documento DOCX
        const doc = new Document({
            sections: [
                {
                    properties: {},
                    children: [
                        // Título do documento
                        new Paragraph({
                            text:
                                documentInfo.document_title ||
                                "DPP - Documento de Proposição de Projeto",
                            heading: HeadingLevel.HEADING_1,
                        }),

                        // Informações da empresa e projeto
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: `${
                                        documentInfo.enterprise || "Empresa"
                                    } | ${documentInfo.project || "Projeto"}`,
                                    bold: false,
                                }),
                            ],
                        }),

                        // Data do documento
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: `Data: ${formattedDate}`,
                                    bold: false,
                                }),
                            ],
                            spacing: { after: 400 },
                        }),

                        // Conteúdo do documento
                        ...(sections.length === 0
                            ? [
                                  new Paragraph({
                                      text: "Nenhuma seção adicionada ao documento.",
                                  }),
                              ]
                            : generateDocxSections(
                                  [...sections].sort(
                                      (a, b) => a.position - b.position
                                  ),
                                  profiles,
                                  budgetData,
                                  tableScopeData
                              )),

                        // Rodapé
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: `Este documento foi gerado em ${formattedDate}.`,
                                    italics: true,
                                }),
                            ],
                            spacing: { before: 400 },
                        }),
                    ],
                },
            ],
        });

        // Gerar e salvar o documento
        const blob = await Packer.toBlob(doc);
        const filename = `${documentInfo.document_title || "documento"}_${
            new Date().toISOString().split("T")[0]
        }.docx`;
        saveAs(blob, filename);

        return Promise.resolve();
    } catch (error) {
        console.error("Erro ao gerar DOCX:", error);
        return Promise.reject(error);
    }
};
